import * as yup from 'yup';
import { normalizeFein } from '../utils/normalize-fein';
import { isValidPhoneInput } from '../utils/phone-to-e164';

const PHONE_E164_MESSAGE =
  'Must be 10 digits, 11 digits starting with 1, or E.164 (e.g. +15551234567)';

const dateString = yup
  .string()
  .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format')
  .required();

const codeRow = yup.object({
  code: yup.string().required(),
  description: yup.string().required(),
});

// One row inside a "Global Fees by Scan Type" fee schedule.
const modalityFeeRow = yup.object({
  description: yup.string().required(),
  global_fee: yup.number().min(0).required(),
});

// `services` map for the GLOBAL_BY_MODALITY flavor. Bucket keys are
// open-ended — modalities are normalized to lowercase snake_case in the
// fee_schedule .transform() below, but the actual key set is whatever the
// contract carries (mri, ct_scan, bone_density, pet_scan, …). Each value is
// still validated as an array of { description, global_fee } rows.
const modalityServices = yup.lazy(value =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? yup.object(
        Object.fromEntries(
          Object.keys(value).map(k => [
            k,
            yup.array(modalityFeeRow).optional(),
          ]),
        ),
      )
    : yup.object().optional(),
);

const practitioner = yup.object({
  name: yup.string().required(),
  npi: yup.string().nullable().defined(),
});

const address = yup.object({
  address_line_1: yup.string().required(),
  address_line_2: yup.string().optional().default(''),
  locality: yup.string().required(),
  region_code: yup.string().required(),
  country_code: yup.string().length(3).default('USA'),
  postal_code: yup.string().required(),
});

const location = yup.object({
  address: address.required(),
  phone: yup
    .string()
    .optional()
    .test('e164', PHONE_E164_MESSAGE, isValidPhoneInput),
  fax: yup
    .string()
    .optional()
    .test('e164', PHONE_E164_MESSAGE, isValidPhoneInput),
  claims_payment_address: yup.string().optional(),
  // Per-location identifiers (PR 1 of the multi-org migration). The parent
  // .transform() below fans out a legacy `shared_identifiers` block onto each
  // location for backward-compat with old fixtures and saved drafts; new input
  // is expected to carry these per-location.
  tax_id: yup
    .string()
    .required()
    .transform(v => (typeof v === 'string' ? normalizeFein(v) : v)),
  group_npi_type2: yup.string().required(),
  medicare_number: yup.string().optional(),
  practitioners: yup.array(practitioner).required(),
});

export const contractInputSchema = yup
  .object({
    client_name: yup.string().required(),
    entity_legal_name: yup.string().required(),
    contract_details: yup
      .object({
        effective_date: dateString,
        term_end: dateString,
        renewal: yup.string().optional(),
        termination_notice_standard: yup.string().optional(),
        termination_notice_fee_schedule_dispute: yup.string().optional(),
      })
      .required(),
    fee_schedule: yup
      .object({
        // Free-form display label, e.g. "Global Fees by Scan Type" or "Case
        // Rate". Informational only — flavor is inferred from `services` vs
        // `codes` presence below, never from this string.
        type: yup.string().optional(),
        rate_per_case: yup.number().optional(),
        currency: yup.string().oneOf(['USD']).optional().default('USD'),
        case_minimum_duration_minutes: yup.number().optional(),
        minimum_category_a_codes: yup.number().optional(),
        pt_engagement_first_case_pct: yup.number().optional(),
        pt_engagement_subsequent_case_pct: yup.number().optional(),
        // Optional for every flavor. The parent .transform() unconditionally
        // backfills `codes: { category_a: [], category_b: [], category_c_excluded: [] }`
        // so downstream code that reads `.codes.category_a.length` keeps
        // working even when the input is unstructured. Per-category schemas
        // still validate row shape when rows are present.
        codes: yup
          .object({
            category_a: yup.array(codeRow).optional(),
            category_b: yup.array(codeRow).optional(),
            category_c_excluded: yup.array(codeRow).optional(),
          })
          .optional(),
        // Derived on save from each row's resolved Item; accepted on read so
        // existing fixtures that include the array still validate.
        covered_modalities: yup.array(yup.string().required()).optional(),
        services: modalityServices.optional(),
      })
      // Universal scaffolding shim. Runs on every fee_schedule regardless of
      // flavor and guarantees two things:
      //   1. ContractInput.fee_schedule.codes is typed as required, so we keep
      //      that promise at runtime by always providing an empty-arrays
      //      shape. Otherwise summary/review code (summarize-input.ts:37,
      //      build-section-summaries.ts) crashes on `.codes.category_a.length`
      //      when the input is modality-flavored or unstructured.
      //   2. When `services` is present, modality buckets are normalized to
      //      lowercase snake_case keys and `fee` is aliased to `global_fee`
      //      so the canonical shape is what the rest of the wizard sees,
      //      regardless of how the contract author cased their keys.
      // No cross-field gate runs after this — see the file-level note at the
      // codes field for the lenient-everywhere classification model.
      .transform(value => {
        if (value == null || typeof value !== 'object') return value;
        const fs = value as Record<string, unknown>;
        const codes = fs.codes as
          | {
              category_a?: unknown;
              category_b?: unknown;
              category_c_excluded?: unknown;
            }
          | undefined;
        const next = { ...fs };
        next.codes = {
          category_a: Array.isArray(codes?.category_a) ? codes.category_a : [],
          category_b: Array.isArray(codes?.category_b) ? codes.category_b : [],
          category_c_excluded: Array.isArray(codes?.category_c_excluded)
            ? codes.category_c_excluded
            : [],
        };
        const rawServices = fs.services;
        if (rawServices != null && typeof rawServices === 'object') {
          const normalized: Record<string, unknown> = {};
          for (const [key, rows] of Object.entries(
            rawServices as Record<string, unknown>,
          )) {
            const k = key.toLowerCase().replace(/[ -]/g, '_');
            if (Array.isArray(rows)) {
              normalized[k] = rows.map(r => {
                if (r == null || typeof r !== 'object') return r;
                const row = r as Record<string, unknown>;
                if (row.global_fee == null && row.fee != null) {
                  return { ...row, global_fee: row.fee };
                }
                return row;
              });
            } else {
              normalized[k] = rows;
            }
          }
          next.services = normalized;
        }
        return next;
      })
      .required(),
    locations: yup.array(location).min(1).required(),
  })
  // Back-compat shim: contract JSON used to carry tax_id / group_npi_type2 /
  // medicare_number under a top-level `shared_identifiers` block. New input
  // (and the wizard's runtime shape) carries them per-location. Detect the
  // legacy shape and fan the values out onto each location that doesn't
  // already specify them. The legacy block is then dropped from the value so
  // the post-cast type matches ContractInput exactly.
  .transform(value => {
    if (value == null || typeof value !== 'object') return value;
    const obj = value as Record<string, unknown>;
    const shared = obj.shared_identifiers as
      | {
          tax_id?: unknown;
          group_npi_type2?: unknown;
          medicare_number?: unknown;
        }
      | undefined;
    if (shared == null) return value;
    const locs = obj.locations;
    if (!Array.isArray(locs)) return value;
    const next = { ...obj };
    next.locations = locs.map(loc => {
      if (loc == null || typeof loc !== 'object') return loc;
      const l = loc as Record<string, unknown>;
      return {
        ...l,
        tax_id: l.tax_id ?? shared.tax_id,
        group_npi_type2: l.group_npi_type2 ?? shared.group_npi_type2,
        medicare_number: l.medicare_number ?? shared.medicare_number,
      };
    });
    delete next.shared_identifiers;
    return next;
  });

export type ContractInputSchemaShape = yup.InferType<
  typeof contractInputSchema
>;
